<!-- ════════════════════════════════════════════════════════════
     PUBLIC HEADER (calendar + default)
     Frosted-glass sticky bar. Used when not on a portal.
     ════════════════════════════════════════════════════════════ -->
@if (!isOperatorPortal() && (isCalendarPortal() || isHomePage() || (!isCustomerPortal() && !isAdminPortal()))) {
  <header class="abx-header abx-header--public" [class.abx-header--menu-open]="menuOpen()">
    <div class="abx-header__inner">
      <!-- Logo -->
      <a routerLink="/home" class="abx-header__logo-link" (click)="closeMenu()">
        <bodyanalytics-brand-logo [variant]="theme.isDark() ? 'dark' : 'light'" [height]="42" />
      </a>

      <!-- Mobile menu toggle — visible below the nav breakpoint only (#1305) -->
      <button
        type="button"
        class="abx-header__toggle"
        [attr.aria-expanded]="menuOpen()"
        aria-controls="abx-header-menu-public"
        aria-label="Toggle navigation menu"
        (click)="toggleMenu()">
        <i class="bx" [class.bx-menu]="!menuOpen()" [class.bx-x]="menuOpen()" aria-hidden="true"></i>
      </button>

      <!-- Collapsible menu (nav links + auth actions). On mobile this becomes a
         dropdown panel revealed by the toggle above. -->
      <div id="abx-header-menu-public" class="abx-header__menu">
        <!-- Nav links — Calendar is an in-app route; the marketing-content links point
           to the live marketing site (the only place the how-it-works / science / faq
           sections reliably exist, since this header also renders on calendar pages
           that have no such sections). Open in a new tab so the app isn't unloaded. -->
        <nav class="abx-header__nav">
          <a routerLink="/calendar/search" class="abx-header__nav-link" (click)="closeMenu()">Calendar</a>
          <a [href]="marketing.howItWorks" target="_blank" rel="noopener noreferrer" class="abx-header__nav-link"
            >How it works</a
          >
          <a [href]="marketing.science" target="_blank" rel="noopener noreferrer" class="abx-header__nav-link"
            >The science</a
          >
          <a [href]="marketing.faq" target="_blank" rel="noopener noreferrer" class="abx-header__nav-link">FAQ</a>
        </nav>

        <!-- Auth actions -->
        <div class="abx-header__actions">
          <abx-dark-mode-toggle />
          @if (isCustomerPortal() && (auth.isAuthenticated$ | async)) {
            <bodyanalytics-member-button />
          }
          <bodyanalytics-nav class="abx-header__nav-auth" />
        </div>
      </div>
    </div>
  </header>
}

<!-- ════════════════════════════════════════════════════════════
     CUSTOMER PORTAL HEADER
     Dark primary blue, tab navigation with amber active indicator.
     ════════════════════════════════════════════════════════════ -->
@if (isCustomerPortal() && !isHomePage()) {
  <header class="abx-header abx-header--portal" [class.abx-header--menu-open]="menuOpen()">
    <div class="abx-header__inner">
      <!-- Logo -->
      <a routerLink="/home" class="abx-header__logo-link" (click)="closeMenu()">
        <img
          src="./assets/logo-horizontal-dark.svg"
          alt="AppliedBioX"
          class="abx-header__logo-img abx-header__logo-img--dark" />
      </a>

      <!-- Mobile menu toggle (#1305) -->
      <button
        type="button"
        class="abx-header__toggle"
        [attr.aria-expanded]="menuOpen()"
        aria-controls="abx-header-menu-customer"
        aria-label="Toggle navigation menu"
        (click)="toggleMenu()">
        <i class="bx" [class.bx-menu]="!menuOpen()" [class.bx-x]="menuOpen()" aria-hidden="true"></i>
      </button>

      <!-- Collapsible menu (tab navigation + portal actions) -->
      <div id="abx-header-menu-customer" class="abx-header__menu">
        <!-- Tab navigation — member-only chrome. Shown only to an onboarded member,
             never on the login / account-creation pages, where an Auth0-authenticated
             visitor may still have no Customer row (#840). -->
        @if ((auth.isAuthenticated$ | async) && !isAuthFlowPage()) {
          <nav class="abx-header__tabs">
            <a
              routerLink="/auth/dashboard"
              routerLinkActive="abx-header__tab--active"
              class="abx-header__tab"
              (click)="closeMenu()"
              >Dashboard</a
            >
            <!-- Cross-app link: the calendar lives on a separate origin (calendarUrl),
               so this must be an external href, not an in-app route — an in-app
               routerLink 404s on the customer domain (#1438). -->
            <a [href]="appConfig.calendarUrl" class="abx-header__tab" (click)="closeMenu()">Calendar</a>
            <a
              routerLink="/auth/account"
              routerLinkActive="abx-header__tab--active"
              class="abx-header__tab"
              (click)="closeMenu()"
              >Account</a
            >
          </nav>
        }

        <!-- Portal actions:
             • authed member on a portal page → profile menu (member chrome).
             • signed-out visitor → Login / Sign-up nav.
             • authed but mid-onboarding on an auth-flow page (/login, /register/*)
               → nothing. The member tabs + profile menu are already hidden here
               (#1678); a not-yet-onboarded customer (customerIdentityGuard →
               /register/account, #840) gets a clean, focused brand bar with no
               sign-out — the account-creation page owns its own flow. -->
        <div class="abx-header__actions">
          <!-- Auth0's isAuthenticated$ has no emission yet while the SDK is
               still resolving the session (silent-auth on a hard reload), so
               the AsyncPipe reads as `null` here — neither `true` nor
               `false`. Checking `!== true` treated that unresolved `null` as
               "signed out" and rendered the Login/Sign-up nav for an
               already-authenticated visitor (#1939). Checking `=== false`
               only matches once Auth0 has explicitly resolved to signed-out. -->
          @if ((auth.isAuthenticated$ | async) && !isAuthFlowPage()) {
            <bodyanalytics-profile-menu />
          } @else if ((auth.isAuthenticated$ | async) === false) {
            <bodyanalytics-nav />
          }
        </div>
      </div>
    </div>
  </header>
}

<!-- ════════════════════════════════════════════════════════════
     ADMIN PORTAL HEADER
     Dark primary blue, operator label.
     ════════════════════════════════════════════════════════════ -->
@if (isAdminPortal()) {
  <header class="abx-header abx-header--portal" [class.abx-header--menu-open]="menuOpen()">
    <div class="abx-header__inner">
      <!-- Logo + operator label -->
      <a routerLink="/home" class="abx-header__brand" (click)="closeMenu()">
        <img
          src="./assets/logo-horizontal-dark.svg"
          alt="AppliedBioX"
          class="abx-header__logo-img abx-header__logo-img--dark" />
        <div class="abx-header__portal-label">Operator</div>
      </a>

      <!-- Mobile menu toggle (#1305) -->
      <button
        type="button"
        class="abx-header__toggle"
        [attr.aria-expanded]="menuOpen()"
        aria-controls="abx-header-menu-admin"
        aria-label="Toggle navigation menu"
        (click)="toggleMenu()">
        <i class="bx" [class.bx-menu]="!menuOpen()" [class.bx-x]="menuOpen()" aria-hidden="true"></i>
      </button>

      <!-- Collapsible menu (admin nav + actions) -->
      <div id="abx-header-menu-admin" class="abx-header__menu">
        <!-- Admin portal navigation -->
        <nav class="abx-header__tabs">
          @if (auth.isAuthenticated$ | async) {
            <bodyanalytics-admin-member-button />
          }
        </nav>

        <div class="abx-header__actions">
          @if ((auth.isAuthenticated$ | async) === false) {
            <bodyanalytics-nav />
          }
          @if (auth.isAuthenticated$ | async) {
            <div class="abx-header__avatar">
              <span class="abx-header__avatar-initials">Op</span>
            </div>
          }
        </div>
      </div>
    </div>
  </header>
}

<!-- ════════════════════════════════════════════════════════════
     OPERATOR PORTAL HEADER
     Dark primary blue, "Operator" label, dark-mode toggle. No marketing nav —
     the field operator app is a focused task shell. Opt-in via [portal]. (#1658)
     ════════════════════════════════════════════════════════════ -->
@if (isOperatorPortal()) {
  <header class="abx-header abx-header--portal abx-header--operator">
    <div class="abx-header__inner">
      <!-- Logo + operator label (mirrors the admin brand block) -->
      <a routerLink="/start" class="abx-header__brand" (click)="closeMenu()">
        <img
          src="./assets/logo-horizontal-dark.svg"
          alt="AppliedBioX"
          class="abx-header__logo-img abx-header__logo-img--dark" />
        <div class="abx-header__portal-label">Operator</div>
      </a>

      <!-- Portal actions: dark-mode toggle only (no marketing nav for operator) -->
      <div class="abx-header__actions">
        <abx-dark-mode-toggle />
      </div>
    </div>
  </header>
}
